import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';




import QuickActionCard from './components/QuickActionCard';
import RecentRecommendationCard from './components/RecentRecommendationCard';
import CreditUsagePanel from './components/CreditUsagePanel';
import QuickAccessCard from './components/QuickAccessCard';
import GuidelinesReferenceCard from './components/GuidelinesReferenceCard';
import ActivityFeedCard from './components/ActivityFeedCard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

import { fetchUserRecommendations } from '../../services/recommendationService';

export default function Dashboard() {
  const { t, ready } = useTranslation();
  const navigate = useNavigate();
  const { profile, loading } = useAuth();
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [filterTimeRange, setFilterTimeRange] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [copySuccess, setCopySuccess] = useState('');
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(true);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Memoize translated values to prevent recalculation
  const translations = useMemo(() => ({
    welcome: t('dashboard.welcome'),
    subtitle: t('dashboard.subtitle'),
    quickActions: t('dashboard.quickActions'),
    recentActivity: t('dashboard.recentActivity'),
    creditsRemaining: t('dashboard.creditsRemaining'),
    guidelines: t('dashboard.guidelines')
  }), [t, ready]);

  // Show loading state while translations are loading
  if (!ready) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>);

  }

  const mockNotifications = [
  {
    id: 1,
    type: "warning",
    priority: "high",
    title: "Low Credit Balance",
    message: "You have 45 credits remaining. Consider upgrading your plan to ensure uninterrupted service.",
    timestamp: "1 hour ago",
    read: false
  },
  {
    id: 2,
    type: "info",
    priority: "normal",
    title: "New Guidelines Available",
    message: "Updated iRefer guidelines for chest imaging are now available in the reference library.",
    timestamp: "3 hours ago",
    read: false
  }];


  const mockGuidelines = [
  {
    id: 1,
    title: "Chest Pain - Cardiac Assessment",
    category: "Cardiovascular"
  },
  {
    id: 2,
    title: "Acute Abdominal Pain",
    category: "Gastrointestinal"
  },
  {
    id: 3,
    title: "Head Injury - Adult",
    category: "Neurological"
  },
  {
    id: 4,
    title: "Lower Back Pain",
    category: "Musculoskeletal"
  }];


  const mockActivities = [
  {
    id: 1,
    type: "approval",
    message: "Your account has been verified and approved",
    timestamp: "1 day ago"
  }];


  useEffect(() => {
    setNotifications(mockNotifications);
    loadRecommendations();
  }, [profile]);

  const loadRecommendations = async () => {
    if (profile?.id) {
      setIsLoadingRecommendations(true);
      try {
        const data = await fetchUserRecommendations(profile?.id, 10);
        // Only set real data - no fallback to mock data
        setRecommendations(data || []);
      } catch (error) {
        console.error('Error loading recommendations:', error);
        setRecommendations([]);
      } finally {
        setIsLoadingRecommendations(false);
      }
    }
  };

  const bodyRegionOptions = [
  { value: 'all', label: t('filters.allRegions') },
  { value: 'head', label: t('bodyRegion.head') },
  { value: 'chest', label: t('bodyRegion.chest') },
  { value: 'abdomen', label: t('bodyRegion.abdomen') },
  { value: 'pelvis', label: t('bodyRegion.pelvis') },
  { value: 'spine', label: t('bodyRegion.spine') },
  { value: 'extremities', label: t('bodyRegion.extremities') }];


  const urgencyOptions = [
  { value: 'all', label: t('filters.allUrgencyLevels') },
  { value: 'emergency', label: t('filters.emergency') },
  { value: 'urgent', label: t('filters.urgent') },
  { value: 'routine', label: t('filters.routine') }];


  const timeRangeOptions = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: '6months', label: 'Last 6 Months' },
  { value: 'year', label: 'This Year' }];


  // Categorize recommendations by time
  const categorizeByTime = (date) => {
    const now = new Date();
    const recDate = new Date(date);
    const diffTime = Math.abs(now - recDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffMonths === 1) return '1 month ago';
    if (diffMonths < 12) return `${diffMonths} months ago`;
    if (diffYears === 1) return '1 year ago';
    return `${diffYears} years ago`;
  };

  const filterByTimeRange = (date) => {
    if (filterTimeRange === 'all') return true;

    const now = new Date();
    const recDate = new Date(date);
    const diffTime = Math.abs(now - recDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    switch (filterTimeRange) {
      case 'today':
        return diffDays === 0;
      case 'week':
        return diffDays <= 7;
      case 'month':
        return diffDays <= 30;
      case '6months':
        return diffDays <= 180;
      case 'year':
        return diffDays <= 365;
      default:
        return true;
    }
  };

  const filteredRecommendations = recommendations?.filter((rec) => {
    const regionMatch = filterRegion === 'all' || rec?.bodyRegion?.toLowerCase() === filterRegion || rec?.body_region?.toLowerCase() === filterRegion;
    const urgencyMatch = filterUrgency === 'all' || rec?.urgency?.toLowerCase() === filterUrgency;
    const timeMatch = filterByTimeRange(rec?.createdAt || rec?.created_at);
    return regionMatch && urgencyMatch && timeMatch;
  });

  // Group recommendations by time category
  const groupedRecommendations = filteredRecommendations?.reduce((groups, rec) => {
    const timeCategory = categorizeByTime(rec?.createdAt || rec?.created_at);
    if (!groups?.[timeCategory]) {
      groups[timeCategory] = [];
    }
    groups?.[timeCategory]?.push(rec);
    return groups;
  }, {});

  const handleViewRecommendation = async (id) => {
    const recommendation = recommendations?.find((r) => r?.id === id);
    if (recommendation) {
      // Fetch complete recommendation details from database
      try {
        const { fetchRecommendationById } = await import('../../services/recommendationService');
        const fullDetails = await fetchRecommendationById(id);
        setSelectedRecommendation(fullDetails);
        setShowDetailsModal(true);
      } catch (error) {
        console.error('Error fetching recommendation details:', error);
        // Fallback to cached data if fetch fails
        setSelectedRecommendation(recommendation);
        setShowDetailsModal(true);
      }
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRecommendation(null);
  };

  const handleCopyRecommendation = async (id) => {
    const recommendation = recommendations?.find((r) => r?.id === id);
    if (recommendation) {
      try {
        const text = `Clinical Recommendation

Patient: ${recommendation?.patientAge || recommendation?.patient_age}y ${recommendation?.patientGender || recommendation?.patient_gender}
Body Region: ${recommendation?.bodyRegion || recommendation?.body_region}
Clinical Presentation: ${recommendation?.clinicalPresentation || recommendation?.clinical_presentation}
Recommended Imaging: ${recommendation?.recommendedImaging || recommendation?.recommended_modality}
Evidence Grade: ${recommendation?.grade || 'B'}
Confidence Score: ${recommendation?.confidenceScore || recommendation?.confidence_score || '85%'}

Generated: ${new Date(recommendation?.date || recommendation?.created_at)?.toLocaleDateString()}`;

        await navigator.clipboard?.writeText(text);
        setCopySuccess('Recommendation copied to clipboard!');
        setTimeout(() => setCopySuccess(''), 3000);
      } catch (error) {
        console.error('Failed to copy:', error);
        alert('Failed to copy recommendation. Please try again.');
      }
    }
  };

  const handleUpgradePlan = () => {
    navigate('/account-settings');
  };

  const handleViewGuideline = (id) => {
    // Navigate to guidelines reference page
    navigate('/guidelines-reference', { state: { selectedGuidelineId: id } });
  };

  const handleBrowseAllGuidelines = () => {
    // Navigate to full guidelines library page
    navigate('/guidelines-reference');
  };

  const handleUserGuide = () => {
    // Navigate to user guide page
    navigate('/user-guide');
  };

  const handleContactSupport = () => {
    // Navigate to contact support page
    navigate('/contact-support');
  };

  const handleNotificationRead = (id) => {
    setNotifications((prev) =>
    prev?.map((notif) => notif?.id === id ? { ...notif, read: true } : notif)
    );
  };

  const handleNotificationDismiss = (id) => {
    setNotifications((prev) => prev?.filter((notif) => notif?.id !== id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>);

  }

  // Calculate credit limit based on subscription tier
  const getCreditLimit = (tier) => {
    const limits = {
      'free': 50,
      'professional': 500,
      'enterprise': 2000
    };
    return limits?.[tier?.toLowerCase()] || 100;
  };

  const isAdminUser = profile?.role === 'admin';
  const creditLimit = isAdminUser ? 999999 : getCreditLimit(profile?.subscription_tier);
  const creditsRemaining = isAdminUser ? 999999 : (profile?.credits_remaining || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-header max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome, {profile?.full_name || 'Doctor'}
          </h1>
        </div>

        {/* Copy Success Message */}
        {copySuccess &&
        <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg">
            <p className="text-sm text-success text-center">{t('dashboard.copySuccess')}</p>
          </div>
        }

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <QuickActionCard
            title={t('dashboard.standardRecommendation')}
            description={t('dashboard.standardRecommendationDesc')}
            iconName="FileText"
            iconColor="var(--color-primary)"
            actionLabel={t('navigation.newRecommendation')}
            actionPath="/clinical-recommendation" />

          <QuickActionCard
            title={t('dashboard.emergencyRequest')}
            description={t('dashboard.emergencyRequestDesc')}
            iconName="AlertCircle"
            iconColor="var(--color-error)"
            actionLabel={t('dashboard.emergencyRequest')}
            actionPath="/clinical-recommendation"
            isEmergency={true}
            badge={t('dashboard.priorityBadge')} />

          <QuickActionCard
            title="Chat with Technologist"
            description="Send real-time messages to the diagnostic imaging technologist handling your referral."
            iconName="MessageCircle"
            iconColor="var(--color-primary)"
            actionLabel="Open Chat"
            actionPath="/doctor-chat" />

        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <QuickAccessCard
            title={t('dashboard.savedRecommendations')}
            value="24"
            iconName="Bookmark"
            iconColor="var(--color-primary)"
            trend={{ direction: 'up', value: '+12%' }}
            onClick={() => console.log('View saved')} />

          <QuickAccessCard
            title={t('dashboard.thisMonth')}
            value="55"
            iconName="TrendingUp"
            iconColor="var(--color-success)"
            trend={{ direction: 'up', value: '+8%' }}
            onClick={() => console.log('View monthly')} />

          <QuickAccessCard
            title={t('dashboard.pdfExports')}
            value="18"
            iconName="FileDown"
            iconColor="var(--color-accent)"
            onClick={() => console.log('View exports')} />

          <QuickAccessCard
            title={t('dashboard.avgResponseTime')}
            value="2.3s"
            iconName="Zap"
            iconColor="var(--color-warning)"
            trend={{ direction: 'down', value: '-15%' }}
            onClick={() => console.log('View metrics')} />

        </div>

        <div className="mb-8">
          <CreditUsagePanel
            creditBalance={creditsRemaining}
            creditLimit={creditLimit}
            subscriptionTier={profile?.subscription_tier || 'Free'}
            isUnlimited={isAdminUser}
            renewalDate={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            })}
            onUpgrade={handleUpgradePlan} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="clinical-card p-6">
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name="History" size={20} color="var(--color-primary)" />
                    <h2 className="text-xl font-semibold text-foreground">{t('dashboard.recentRecommendations')}</h2>
                  </div>
                </div>
                {filteredRecommendations?.length > 0 &&
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                    options={bodyRegionOptions}
                    value={filterRegion}
                    onChange={setFilterRegion}
                    placeholder={t('dashboard.filterByRegion')} />

                    <Select
                    options={urgencyOptions}
                    value={filterUrgency}
                    onChange={setFilterUrgency}
                    placeholder={t('dashboard.filterByUrgency')} />

                    <Select
                    options={timeRangeOptions}
                    value={filterTimeRange}
                    onChange={setFilterTimeRange}
                    placeholder="Filter by time" />

                  </div>
                }
              </div>

              {isLoadingRecommendations ?
              <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                  <p className="text-muted-foreground">{t('dashboard.loadingRecommendations')}</p>
                </div> :
              filteredRecommendations?.length > 0 ?
              <>
                  {Object.entries(groupedRecommendations)?.map(([timeCategory, recs]) =>
                <div key={timeCategory} className="mb-6">
                      <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                        <Icon name="Clock" size={16} />
                        {timeCategory}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recs?.map((recommendation) =>
                    <RecentRecommendationCard
                      key={recommendation?.id}
                      recommendation={recommendation}
                      onView={handleViewRecommendation}
                      onCopy={handleCopyRecommendation}
                      timeCategory={timeCategory} />

                    )}
                      </div>
                    </div>
                )}
                  <div className="flex justify-center mt-6">
                    <Button
                    variant="outline"
                    iconName="MoreHorizontal">

                      {t('dashboard.loadMoreRecommendations')}
                    </Button>
                  </div>
                </> :

              <div className="col-span-2 flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Icon name="FileText" size={32} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{t('dashboard.noRecommendationsYet')}</h3>
                  <p className="text-muted-foreground text-center max-w-md mb-6">
                    {t('dashboard.noRecommendationsDesc')}
                  </p>
                  <Button
                  onClick={() => navigate('/clinical-recommendation')}
                  iconName="Plus"
                  iconPosition="left">

                    {t('dashboard.createFirstRecommendation')}
                  </Button>
                </div>
              }
            </div>

            <ActivityFeedCard activities={mockActivities} />
          </div>

          <div className="space-y-6">
            <GuidelinesReferenceCard
              guidelines={mockGuidelines}
              onViewGuideline={handleViewGuideline}
              onBrowseAll={handleBrowseAllGuidelines} />


            {/* iRefer Platform Integration Card */}
            <div className="clinical-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="ExternalLink" size={20} color="var(--color-primary)" />
                <h3 className="text-lg font-semibold text-foreground">iRefer Platform</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Access the iRefer referral platform directly to review referred cases and manage patient imaging requests.

              </p>
              <Button
                variant="outline"
                fullWidth
                iconName="Eye"
                iconPosition="left"
                onClick={() => window.open('https://www.irefer.org.uk/', '_blank')}>

                Open iRefer Platform
              </Button>
              <div className="mt-3 p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <Icon name="Info" size={14} className="inline mr-1" />
                  You will need to sign in with your iRefer credentials to access the platform.
                </p>
              </div>
            </div>

            <div className="clinical-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Shield" size={20} color="var(--color-success)" />
                <h3 className="text-lg font-semibold text-foreground">{t('dashboard.safetyMetrics')}</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-success/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                    <span className="text-sm font-medium text-foreground">{t('dashboard.complianceRate')}</span>
                  </div>
                  <span className="text-lg font-bold text-success">98.5%</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="Radiation" size={16} color="var(--color-primary)" />
                    <span className="text-sm font-medium text-foreground">{t('dashboard.avgRadiationDose')}</span>
                  </div>
                  <span className="text-lg font-bold text-primary">2.4 mSv</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="Target" size={16} color="var(--color-accent)" />
                    <span className="text-sm font-medium text-foreground">{t('dashboard.accuracyScore')}</span>
                  </div>
                  <span className="text-lg font-bold text-accent">96.2%</span>
                </div>
              </div>
            </div>

            <div className="clinical-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="HelpCircle" size={20} color="var(--color-primary)" />
                <h3 className="text-lg font-semibold text-foreground">{t('dashboard.quickHelp')}</h3>
              </div>

              <div className="space-y-2">
                <Button
                  variant="ghost"
                  fullWidth
                  iconName="BookOpen"
                  iconPosition="left"
                  className="justify-start"
                  onClick={handleUserGuide}>

                  {t('dashboard.userGuide')}
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="justify-start"
                  onClick={handleContactSupport}>

                  {t('dashboard.contactSupport')}
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  iconName="Video"
                  iconPosition="left"
                  className="justify-start">

                  {t('dashboard.videoTutorials')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

}